/**
 * @section 4.1.9.3
 */
import { Theme } from "@material-ui/core";
import * as registry from "@qpr/client-api-registry";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useAppContext } from "./../../context/AppCtxProvider";
import { makeThemedStyles } from "./../../viewcomponents/utils";
import { FileValue } from "./FileValue";
import UploadedFilesList from "./UploadedFilesList";
const useStyles = makeThemedStyles(
  "MilestoneInput.FileUploadInput",
  (theme: Theme) => ({
    documentDownload: {
      position: "relative",
      marginTop: "1em",
      marginRight: "0.3em",
      padding: "0.5em",
      color: "#4C5157",
      borderStyle: "solid",
      borderColor: "rgb(232,234,237)",
      borderRadius: "8px",
      borderWidth: "1px",
      textAlign: "center",
      fontSize: "1.5em",
      minWidth: "200px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      background: "rgb(250,250,250)",
      justifyContent: "space-between",
    },
    uploadFileIcon: {
      width: "30px",
      height: "30px",
    },
    visibilityIcon: {
      width: "20px",
      height: "20px",
      marginLeft: "0.8em",
      paddingLeft: "0.3em",
      verticalAlign: "middle",
      borderLeft: "1px solid #D1E2EA",
      color: "#8FB8CC",
    },
    closeIcon: {
      width: "20px",
      height: "20px",
      paddingLeft: "0.3em",
      verticalAlign: "middle",
      color: "#8FB8CC",
    },
    documentUpload: {
      marginTop: "1em",
      padding: "0.5em",
      borderStyle: "dashed",
      borderColor: "#D1D5DB",
      borderRadius: "0.15em",
      borderWidth: "0.15em",
      fontSize: "16px",
      color: "#888",
      textAlign: "center",
      cursor: "pointer",
      "& p": {
        margin: 0,
      },
    },
    dragActive: {
      backgroundColor: "#FAFAFA",
    },
    error: {
      color: "rgb(241, 86, 66)",
      margin: "2px 0px 0px",
      fontSize: "14px",
    },
  })
);
type IFileUploadErrors = {
  type: string;
  size: string;
  files: string;
  name: string;
};
interface Props {
  maxFiles?: number;
  maxSize?: number;
  accept?: string[];
  errorMessages?: boolean;
  files?: FileValue[];
  disabled?: boolean;
  onErrors?: (errors: IFileUploadErrors) => void;
  onFiles: (files: File[]) => void;
}
const FileUploadInput = (props: Props) => {
  const appCtx = useAppContext();
  const { onFiles, onErrors, maxFiles = 1, accept, errorMessages } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<IFileUploadErrors>({
    size: "",
    type: "",
    files: "",
    name: "",
  });
  const classes = useStyles();
  // default 10MB
  const maxSize = props.maxSize ? props.maxSize * 1000000 : 10 * 1000000;
  const handleErrors = (
    errorType: "size" | "type" | "reset" | "files" | "name"
  ) => {
    setErrors((errors) => {
      switch (errorType) {
        case "size":
          return {
            ...errors,
            size: `Maximum ${maxSize / 1000000} MB files can be uploaded`,
          };
        case "type":
          return {
            ...errors,
            type: `File type must be one of ${accept && accept.join(", ")}`,
          };
        case "files":
          return { ...errors, files: `Maximum ${maxFiles} files allowed` };
        case "name":
          return {
            ...errors,
            name: "Cannot add multiple files with same name",
          };
        default:
          return { size: "", type: "", files: "", name: "" };
      }
    });
  };
  const { isDragActive, getRootProps, getInputProps } = useDropzone({
    accept,
    onDrop: (acceptedFiles, fileRejections) => {
      if (props.disabled) return;
      handleErrors("reset");
      const errorCode = fileRejections[0]?.errors[0]?.code;
      if (errorCode && errorCode === "file-too-large") {
        if (fileRejections.length + files.length > maxFiles)
          handleErrors("files");
        else handleErrors("size");
      }
      if (errorCode && errorCode === "file-invalid-type") {
        handleErrors("type");
      }
      setFiles((files) => {
        if (
          files.length >= maxFiles ||
          acceptedFiles.length + files.length > maxFiles
        ) {
          handleErrors("files");
          return files;
        }
        const filesList = [...files, ...acceptedFiles];
        const fileSize = filesList.reduce(function (acc, current) {
          acc += current.size;
          return acc;
        }, 0);

        const tmp = files.find((fle) =>
          acceptedFiles.find((f) => f.name === fle.name)
        );
        if (tmp) {
          handleErrors("name");
          return files;
        }
        if (maxSize && fileSize > maxSize) {
          handleErrors("size");
          return files;
        }
        return filesList;
      });
    },
  });
  useEffect(() => {
    onFiles(files);
  }, [files.length]);
  useEffect(() => {
    if (onErrors !== undefined) {
      onErrors(errors);
    }
  }, [errors.files, errors.size, errors.type, errors.name]);
  useEffect(() => {
    setFiles([]);
  }, [appCtx.elementId, appCtx.period]);
  const handleFileRemove = (fileIndex: number) => {
    setFiles((files) => files.filter((_, index) => index !== fileIndex));
  };
  const PaperPlusIcon = registry.getSvg("PaperPlusIcon");
  return (
    <div>
      <UploadedFilesList
        files={files}
        values={props.files}
        readOnly={props.disabled}
        handleFileRemove={handleFileRemove}
      />
      <div
        className={`${classes.documentUpload} ${
          isDragActive ? classes.dragActive : ""
        }`}
        {...getRootProps()}
      >
        <input {...getInputProps()} disabled={props.disabled} />
        <PaperPlusIcon className={classes.uploadFileIcon} />
        <p>
          {t(`Upload ${maxFiles === 1 ? "a file" : "files"} or drag and drop`)}
        </p>
        <p>
          {t("uploadSize", {
            fileType: accept
              ? accept.map((s) => s.slice(1).toUpperCase()).join(",")
              : t("files"),
            maxSize: `${maxSize / 1000000}MB`,
          })}
        </p>
      </div>
      {(errorMessages || errorMessages === undefined) && (
        <>
          {errors.type && <p className={classes.error}>{errors.type}</p>}
          {errors.size && <p className={classes.error}>{errors.size}</p>}
          {errors.files && <p className={classes.error}>{errors.files}</p>}
          {errors.name && <p className={classes.error}>{errors.name}</p>}
        </>
      )}
    </div>
  );
};
export default FileUploadInput;
